import {
  Component,
  h,
  Element,
  Host,
  Prop,
  Watch,
  State,
  Listen,
  Method,
  EventEmitter,
  Event,
  Build,
  writeTask,
  ComponentInterface,
  readTask,
} from '@stencil/core';
import algoliasearch, {
  SearchClient as AlgoliaClient,
  SearchIndex as AlgoliaIndex,
} from 'algoliasearch';
import { getDirectChildren } from '../../utils/dom';
import { PromiseQueue } from '../../utils/promise-queue';
import {
  ComponentStore,
  StorageMethods,
} from '../../utils/store/component-store';
import { Squirrel } from '../../utils/template';
import { debounce } from '../../utils/throttle';
import { Universe, SearchChangeEvent } from './algolia-data';
import type {
  AlgoliaMultiResult,
  IndexResult,
  AlgoliaResultHit,
  FilterChangeEventDetail,
  PageChangeEventDetail,
  AppliedFilter,
  AlgoliaFacet,
  SearchIndex,
} from '../../interface';

interface FacetFilterQueryBlock {
  or: Array<string>;
  and: Array<string>;
  not: Array<string>;
  name: string;
}
interface FacetFilter extends FilterChangeEventDetail {
  element: HTMLNanoAlgoliaFilterElement;
}
type FilterGroup = { [prop: string]: FacetFilter };

/**
 * Algolia search container to contain and sync nano-algolia-results and nano-algolia-filter components.
 * Can optionally contain nano-algolia-input components or use them as a 'resultsEmitter'
 * @slot search-input - optional. Should contain an algolia-input component.
 * @slot loader - optional. Elements to show when search results are loading.
 * @slot template - optional. Template string (format depends on 'tplEngine' property). May contain other algolia-... components with nested templates.
 * @slot output - Search result output. Will be a placeholder for template output or optionally nest algolia-... components.
 */
@Component({
  tag: 'nano-algolia',
  shadow: true,
  styleUrl: 'algolia.scss',
})
export class Algolia implements ComponentInterface {
  // filters that have an initial value
  private staticFacetFilters: FilterGroup = {};
  private dynFacetFilters: FilterGroup = {};
  private tplSlot: HTMLElement;
  private outputSlot: HTMLElement;
  private slottedInput: HTMLElement;
  private hasLoadSlot: boolean = false;
  private resultsDiv: HTMLDivElement;
  private defaultDiv: HTMLDivElement;
  private tpl: string;
  private facets: AlgoliaFacet;
  private placedAlgoliaEles: Element[] = [];
  private algoliaClient: AlgoliaClient;
  // lots of stuff happens asynchronously. This makes sure they fire in the corrent order
  private promiseQueue = new PromiseQueue();

  @Element() host: HTMLNanoAlgoliaElement;

  // Internal state

  // filters that change via a template. They have no initial value
  @State() _dynFacetFilters: FilterGroup = {};
  @State() inputField: HTMLNanoInputElement | HTMLInputElement;
  @State() algoliaIndex: AlgoliaIndex;
  @State() indeces: IndexResult[] = [];
  @State() resultsEmitterEle: HTMLElement;
  @State() indexResults: IndexResult;
  @State() filterChanged: string = '';
  @State() currentHits: AlgoliaResultHit[];
  @State() changeEvent: SearchChangeEvent = SearchChangeEvent.Init;
  @State() isLoading: boolean = false;
  @State() wormholeState: any = this;

  // Public props

  /**
   * Show the result panel. By default it will automatically show when the result receives new hits.
   */
  @Prop({ mutable: true }) showResults: boolean = false;

  /**
   * The current algolia page of results currently being viewed or to get.
   */
  @Prop({ mutable: true }) resultsPage: number = 0;

  /**
   * An Algolia App ID key. Used in conjunction with 'searchIndex' & 'apiKey'.
   */
  @Prop({ mutable: true }) appId: string;

  /**
   * An Algolia API Key. Used in conjunction with 'searchIndex' & 'appId'.
   */
  @Prop({ mutable: true }) apiKey: string;

  /**
   * Algolia search index. Used in conjunction with 'apiKey' & 'appId'.
   * ```
   * { index: string, // algolia index name
   *   domain?: string, // used in template filters to resolve incomplete url fields
   *   filters?: [string], // algolia facets to retrive
   *   hitsPerPage?: number,
   *   name?: string // human readable index name
   * }
   * ```
   */
  @Prop({ mutable: true }) searchIndex: SearchIndex = {} as SearchIndex;

  /**
   * Algolia search index name. Alternative to 'searchIndex'.
   * A simplified shortcut for setting an index.
   * Properties such as hitsPerPage and facets will come from the algolia index
   */
  @Prop({ mutable: true }) searchIndexName: string = '';

  /**
   * JS querSelector string of a `nano-input` / `input` element from which
   * to listen to `nano-change` or `change` events and change the `query` prop.
   * Not required if an appropriate field is in the 'search-input' slot
   */
  @Prop() listenTo?: string = null;

  /**
   * The query string used to search the index. Will automtically be set with any slotted
   * `search-input` input elements or from elements linked to from `listen-to`
   */
  @Prop({ mutable: true }) query: string = '';

  /**
   * The external operator joining filter values.
   */
  @Prop() operator: 'or' | 'and' = 'and';

  /**
   * Query filters to apply to all searches.
   * Refer to https://www.algolia.com/doc/api-reference/api-parameters/filters for format.
   */
  @Prop() filters: Array<string> = [];

  /**
   * Template engine to use when rendering results and filters.
   * Defaults to Squirrel v8 (https://squirrelly.js.org/). This gives you access
   * to the underlying squirrel object allowing you to modify it's configuration / add filters.
   */
  @Prop() tplEngine: Object = Squirrel;

  /**
   * Template engine render function to use when rendering results and filters.
   * Defaults to Squirrel v8's render (https://squirrelly.js.org/).
   */
  @Prop() tplRenderFn: (...args: any[]) => string = Squirrel.render;

  /**
   * Changes the index without removing any current filters. Most appropriate for algolia replicas.
   */
  @Prop({ mutable: true }) replicaIndex: string = '';

  /**
   * Will maintain a search all / '*' on init and reset allowing for a view
   * then filter experience rather than a search first experience
   */
  @Prop({ mutable: true }) browseIndex: boolean = false; // eslint-disable-line @stencil/strict-mutable

  /**
   * Minimum number of characters before performing a search
   */
  @Prop() minChars: number = 2;

  /**
   * Store search queries (against this ID) to the component store.
   * Use in conjunction with storeMethod
   */
  @Prop() storeId?: string;

  /**
   * The method of storage.
   * Either session storage, url hash (after the '#') or url query (after the '?').
   */
  @Prop() storeMethod: StorageMethods = 'session';

  // Events

  /**
   * Emitted when the algolia element has been created.
   */
  @Event() nanoDidLoad!: EventEmitter<HTMLNanoAlgoliaElement>;

  /**
   * Emitted when results and all filters are shown and have loaded.
   */
  @Event({ bubbles: false }) nanoResultsShown!: EventEmitter<IndexResult>;

  /**
   * Emitted before an algolia search.
   */
  @Event() nanoBeforeQuery!: EventEmitter<IndexResult>;

  /**
   * Emitted after an algolia search.
   */
  @Event() nanoAfterQuery!: EventEmitter<IndexResult>;

  /**
   * Emitted after an results have been updated
   */
  @Event() nanoNewResults!: EventEmitter<IndexResult>;

  // Public methods

  /**
   * @param facetName - will clear all filter selections of that type
   * @param facetValue - will remove a single filter selection
   * Remove facet filters (as set from nano-algolia-filter elements).
   * The omission or both will remove all facet filters
   */
  @Method()
  async removeFilters(facetName?: string, facetValue?: string) {
    if (!facetName && !facetValue) this.clearAllFacetFilters();
    else {
      Object.keys(this._dynFacetFilters).forEach((filterId) => {
        if (
          facetName &&
          this._dynFacetFilters[filterId].facetName === facetName
        ) {
          if (!facetValue) this._dynFacetFilters[filterId].element.value = '';
          else {
            this._dynFacetFilters[filterId].element.value =
              this._dynFacetFilters[filterId].value.filter(
                (val) => val !== facetValue
              );
          }
        }
      });
    }
  }

  // Listeners to external events

  // a child nano-algolia-... updated their template
  @Listen('nanoTplUpdated')
  onAllTplUpdate(ev: CustomEvent & { target: HTMLElement }) {
    ev.stopPropagation();

    if (ev.target.tagName === 'NANO-ALGOLIA-RESULTS') {
      requestAnimationFrame(() => {
        this.nanoNewResults.emit(this.indexResults);
      });
    }
  }

  // a nano-algolia-pagination or nano-algolia-results changed the page
  @Listen('nanoPageChanged')
  async onPageChange(ev?: CustomEvent) {
    if (!this.indexResults) return;
    let page = this.resultsPage;
    if (ev) {
      ev.stopPropagation();
      page = (ev.detail as PageChangeEventDetail).page;
    }

    page = Math.min(this.indexResults.totalPages - 1, page);
    page = Math.max(page, 0);

    if (this.indexResults.currentPage === page) return;

    this.indexResults.currentPage = page;
    await this.algoliaSearch(SearchChangeEvent.Page);
  }

  /**
   * nano-algolia-filter changed value.
   * If that happened before a indexResults were setup, it was a static filter.
   * Static filters persist accross search changes
   */
  @Listen('nanoFilterChanged')
  async onFilterChange(ev: CustomEvent) {
    ev.stopPropagation();

    const dynFilterObj: FilterGroup = {};
    const filter = ev.detail as FacetFilter;
    filter.element = ev.target as HTMLNanoAlgoliaFilterElement;

    if (this.indexResults) {
      if (this.staticFacetFilters[filter.filterId])
        this.staticFacetFilters[filter.filterId] = filter;
      else {
        dynFilterObj[filter.filterId] = filter;
        this._dynFacetFilters = { ...this._dynFacetFilters, ...dynFilterObj };
      }

      this.indexResults.appliedFilters = this.makeAppliedFilters();
    } else this.staticFacetFilters[filter.filterId] = filter;

    this.filterChanged =
      filter.value && filter.value.length ? filter.filterId : 'all';

    this.resetPage();
    this.algoliaSearch(SearchChangeEvent.Filter);
  }

  // Internal state watchers

  // Filters can fire a change event, cause a re-render then get detached.
  // This can cause filter dupes. Check to see if the original element is still in the DOM.
  @Watch('_dynFacetFilters')
  dynFacetFiltersChange() {
    Object.values(this._dynFacetFilters).forEach((filter) => {
      if (this.host.ownerDocument === filter.element.ownerDocument)
        this.dynFacetFilters[filter.filterId] = filter;
      else delete this.dynFacetFilters[filter.filterId];
    });
  }

  @Watch('browseIndex')
  browseIndexChange() {
    if (
      this.browseIndex &&
      (!this.query || this.query.length < this.minChars)
    ) {
      this.query = '*';
    }
  }

  @Watch('inputField')
  inputFieldSet() {
    if (!this.inputField) return;
    if (
      this.query !== '*' &&
      this.inputField &&
      this.inputField.value !== this.query
    )
      this.inputField.value = this.query;
  }

  @Watch('query')
  queryChange() {
    this.searchChange();
  }
  private searchChange = async () => {
    if (!this.algoliaIndex) return;

    if (!this.query || this.query.length < this.minChars) {
      if (!this.browseIndex) {
        if (!this.query || this.query.length === 0) {
          this.handleSearchReset();
          return;
        } else return;
      } else if (this.query !== '*') {
        this.query = '*';
        return;
      }
    }
    // Do an initialise search (setup searchIndex)
    this.isLoading = true;

    setTimeout(async () => {
      // Clear dynamic filters and reset page
      this.resetPage();

      await this.algoliaSearch(SearchChangeEvent.Init);
      // Populate master content - optionally from template
      await this.updateContent();

      setTimeout((_) => (this.isLoading = false), 800);
      this.showResults = true;
      this.nanoResultsShown.emit(this.indexResults);
    }, 200);
  };

  @Watch('replicaIndex')
  async internalIndexSwitch() {
    if (
      !this.replicaIndex ||
      !this.indexResults ||
      this.indexResults.index === this.replicaIndex
    )
      return;
    this.indexResults.index = this.replicaIndex;
    this.algoliaIndex = this.algoliaClient.initIndex(this.indexResults.index);
    this.resetPage();
    this.algoliaSearch(SearchChangeEvent.Replica);
  }

  @Watch('resultsPage')
  pageChange() {
    this.onPageChange();
  }

  @Watch('searchIndexName')
  searchIndexNameChange() {
    if (
      !this.searchIndexName ||
      !this.searchIndexName.length ||
      (this.searchIndex && this.searchIndex.index === this.searchIndexName)
    )
      return;
    this.searchIndex = { index: this.searchIndexName };
  }

  @Watch('apiKey')
  @Watch('appId')
  initAlgoliaClient() {
    if (!this.appId || !this.apiKey) return;
    this.algoliaClient = algoliasearch(this.appId, this.apiKey);
  }

  @Watch('searchIndex')
  initAlgoliaIndex() {
    if (!this.algoliaClient || !this.searchIndex) return;
    if (this.searchIndex.index) {
      if (this.replicaIndex) this.searchIndex.index = this.replicaIndex;
      this.searchIndexName = this.searchIndex.index;
      this.algoliaIndex = this.algoliaClient.initIndex(this.searchIndex.index);
      this.searchChange();
    }
  }

  @Watch('showResults')
  showResultsChange() {
    if (!this.resultsDiv || !this.defaultDiv) return;

    if (this.showResults) {
      this.resultsDiv.style.width = this.host.scrollWidth + 'px';
      this.resultsDiv.style.minHeight = this.host.scrollHeight + 'px';

      writeTask(() => {
        this.resultsDiv.addEventListener('transitionend', this.onResultDisplay);
        this.resultsDiv.style.display = 'block';
        setTimeout(() => this.resultsDiv.classList.add('show'), 50);
      });
    } else {
      writeTask(() => {
        this.resultsDiv.classList.remove('shown');
        this.defaultDiv.style.display = 'block';
        setTimeout(() => {
          this.resultsDiv.addEventListener(
            'transitionend',
            this.onResultDisplay
          );
          this.resultsDiv.classList.remove('show');

          if (!!this.slottedInput)
            this.slottedInput.focus({ preventScroll: true });
          else this.defaultDiv.focus({ preventScroll: true });
        }, 50);
      });
    }
  }

  @Watch('filters')
  handleFilterpropChange() {
    this.algoliaSearch(SearchChangeEvent.Init);
  }

  // Internal methods

  private fixDomain(results: AlgoliaMultiResult) {
    if (!this.searchIndex || !this.searchIndex.domain) return results;
    results.hits.map((hit) => {
      if (hit.url && !hit.url.match(/^http/))
        hit.url = location.protocol + '//' + this.searchIndex.domain + hit.url;
    });
    return results;
  }

  // Takes all current filters (dynamic and static)
  // and merges common filters' values togther
  private makeAppliedFilters = (): AppliedFilter[] => {
    const appliedFtrs: AppliedFilter[] = [];

    const makeAppliedFilter = (facetFilters: {
      [prop: string]: FacetFilter;
    }) => {
      Object.values(facetFilters).forEach((filter) => {
        if (filter.value && filter.value.length) {
          const found = appliedFtrs.find(
            (appFilter) => appFilter.name === filter.facetName
          );
          if (!found)
            appliedFtrs.push({
              name: filter.facetName,
              values: [...filter.value],
            });
          else found.values = [...found.values, ...filter.value];
        }
      });
    };
    makeAppliedFilter(this.dynFacetFilters);
    makeAppliedFilter(this.staticFacetFilters);

    if (appliedFtrs.length) return appliedFtrs;
    return null;
  };

  // Queue up a search
  private algoliaSearch = async (eventType: SearchChangeEvent) => {
    return this.promiseQueue.enqueue(() => this.queueSearch(eventType));
  };

  // Do a search
  private queueSearch = async (eventType: SearchChangeEvent) => {
    if (
      !this.algoliaIndex ||
      !this.query ||
      !this.searchIndex ||
      !this.searchIndex.index
    )
      return;

    this.nanoBeforeQuery.emit(this.indexResults);

    // clear all facet filters on init (new query)
    if (SearchChangeEvent.Init === eventType) this.clearAllFacetFilters();

    const searchParams: any = {
      filters: this.algoliaFilterStr(),
      page: this.indexResults ? this.indexResults.currentPage : 0,
    };

    if (this.searchIndex.filters)
      searchParams.facets = this.searchIndex.filters;
    if (this.searchIndex.hitsPerPage)
      searchParams.hitsPerPage = this.searchIndex.hitsPerPage;

    let result = (await this.algoliaIndex.search(
      this.query,
      searchParams
    )) as AlgoliaMultiResult;
    result = this.fixDomain(result);

    // set event type - this informs children components why things changed
    this.changeEvent = eventType;

    // Setup initial meta on new queries. Some of it should not change on filter / pagination
    if (eventType === SearchChangeEvent.Init || !this.indexResults) {
      this.indexResults = {
        currentPage: 0,
        totalHits: result.nbHits,
        hitsPerPage: null,
        origFilters: result.facets,
        query: this.query,
        domain: this.searchIndex.domain || null,
        totalHitsWithFilters: 0,
        results: [],
        totalPages: 0,
        appliedFilters: this.makeAppliedFilters(),
        indexName: this.searchIndex.name || null,
        index: this.searchIndex.index,
      };
    }

    this.indexResults.dynFilters = result.facets;
    this.indexResults.results = result.hits as AlgoliaResultHit[];
    this.indexResults.totalHitsWithFilters = result.nbHits;
    this.indexResults.currentPage = result.page;
    this.indexResults.totalPages = result.nbPages;
    this.indexResults.hitsPerPage = result.hitsPerPage;
    this.indexResults.query = result.query;
    // console.log('I GOT NEW RESULTS', eventType, this.query, this.algoliaFilterStr(), this.indexResults.results.length, this.indexResults.currentPage )

    if (
      this.changeEvent !== SearchChangeEvent.Page &&
      this.changeEvent !== SearchChangeEvent.Replica
    ) {
      this.facets = result.facets;
    }
    this.nanoAfterQuery.emit(this.indexResults);

    // current hits and page will cause re-renders in children components.
    // Wait a tick before firing otherwise events can fire in the wrong order
    return new Promise((resolve) => {
      setTimeout(() => resolve('tpl updated'), 20);
    }).then((_) => {
      this.currentHits = this.indexResults.results;
      this.resultsPage = this.indexResults.currentPage;
    });
  };

  private resetPage() {
    if (!this.indexResults) return;
    this.indexResults.currentPage = 0;
  }

  private clearAllFacetFilters() {
    Object.values(this._dynFacetFilters).forEach((filter) => {
      filter.element.value = '';
    });
    this._dynFacetFilters = {};
  }

  private algoliaFilterStr() {
    // groups applied facet filters (from algolia-filter components)
    // with their applied, 'internal' operators (and, or, not)

    const facetFilterObj: { [prop: string]: FacetFilterQueryBlock } = {};

    const groupFilters = (facetFilters: { [prop: string]: FacetFilter }) => {
      let filter: FilterChangeEventDetail;
      let filterOp: Array<string>;

      Object.keys(facetFilters).forEach((filterId) => {
        filter = facetFilters[filterId];
        facetFilterObj[filterId] = facetFilterObj[filterId] || {
          name: filter.facetName,
          or: [],
          and: [],
          not: [],
        };

        filterOp = facetFilterObj[filterId][filter.operator];
        if (filter.value && filter.value.length) {
          facetFilterObj[filterId][filter.operator] = [
            ...filterOp,
            ...filter.value,
          ];
        }
      });
    };

    groupFilters(this.dynFacetFilters);
    groupFilters(this.staticFacetFilters);

    const formatVal = (name: string, val: string) => {
      if (val.indexOf(' ') > -1) return name + ':"' + val + '"';
      else return name + ':' + val;
    };
    let filterStrParts: Array<string> = [];
    let operatorGrp: FacetFilterQueryBlock;
    let filterName: string;
    // let processedFilters: string[] = [];

    // joins all applied facet filter operator groups with the set 'outer'
    // operator into an algolia filter query string
    Object.values(facetFilterObj).forEach(function (filter) {
      Object.keys(filter)
        .filter((filterProp) => filterProp.match(/(or|and|not)/))
        .forEach((operator) => {
          operatorGrp = filter;
          filterName = filter.name;

          if (operator === 'or' || operator === 'and') {
            if (operatorGrp[operator].length) {
              filterStrParts.push(
                '( \
                ' +
                  operatorGrp[operator]
                    .map((val) => {
                      if (val.length) return formatVal(filterName, val);
                    })
                    .join(' ' + operator.toLocaleUpperCase() + ' ') +
                  ' \
              )'
              );
            }
          } else {
            if (operatorGrp.not.length) {
              filterStrParts.push(
                '( \
                ' +
                  operatorGrp[operator]
                    .map((val) => {
                      if (val.length)
                        return 'NOT ' + formatVal(filterName, val);
                    })
                    .join(' AND ') +
                  ' \
              )'
              );
            }
          }
        });
    });

    // add in any static applied filters from filters prop
    filterStrParts = [...this.filters, ...filterStrParts];
    // console.log('filterStr:', filterStrParts.join(' ' + this.operator.toLocaleUpperCase() + ' '))
    return filterStrParts.join(' ' + this.operator.toLocaleUpperCase() + ' ');
  }

  @Listen('nanoChange', { target: 'body' })
  @Listen('change', { target: 'body' })
  handleInputChange(ev: CustomEvent | (Event & { target: HTMLElement })) {
    if (ev.target !== this.inputField) return;
    this.query = this.inputField.value;
  }

  private handleSearchReset = () => {
    this.showResults = false;
    this.indexResults = null;
  };

  private onResultDisplay = () => {
    writeTask(() => {
      this.resultsDiv.removeEventListener(
        'transitionend',
        this.onResultDisplay
      );
      if (!this.showResults) this.resultsDiv.style.display = 'none';
      else {
        this.defaultDiv.style.display = 'none';
        this.resultsDiv.classList.add('shown');
        this.resultsDiv.style.minHeight = '';
        this.resultsDiv.style.width = '';
      }
    });
  };

  private async updateContent() {
    return this.promiseQueue.enqueue(() => this.updateContentQueue());
  }

  private async updateContentQueue() {
    const outputTo = this.outputSlot || this.resultsDiv;

    if (!outputTo || !this.tpl) return 'no tpl';

    const tmpCont = document.createElement('div');
    tmpCont.innerHTML = this.tpl;

    const algoliaEles: Element[] = [];
    this.placedAlgoliaEles = [];

    // take out children algolia elements 'cos they have their own templates
    // don't want to swap in their templates
    Array.from(
      tmpCont.querySelectorAll(
        'nano-algolia, nano-algolia-filter, nano-algolia-results'
      )
    ).forEach((el, i) => {
      const placeholder = document.createElement('div');
      placeholder.dataset.placeholderId = i.toString();
      placeholder.classList.add('nano-ele-placeholder');
      algoliaEles.push(el);
      el.parentNode.replaceChild(placeholder, el);
    });

    // swap in template content
    // (all this nonsense htmlencodes any < or >)

    outputTo.innerHTML = this.tplRenderFn(
      tmpCont.innerHTML
        .replace(/&gt;/gm, '>')
        .replace(/&lt;/gm, '<')
        .replace(/&amp;/gm, '&'),
      this.indexResults
    );

    // put back in algolia elements
    Array.from(outputTo.querySelectorAll('div.nano-ele-placeholder')).forEach(
      (el: HTMLDivElement) => {
        el.parentNode.replaceChild(
          algoliaEles[parseInt(el.dataset.placeholderId)],
          el
        );
        this.placedAlgoliaEles.push(
          algoliaEles[parseInt(el.dataset.placeholderId)]
        );
      }
    );

    if (!this.placedAlgoliaEles.length) return Promise.resolve('algolia setup');

    // wait for all children algolia elements to resolve before calling 'done'
    return new Promise((resolve) => {
      const childrenResolver = (ev: CustomEvent) => {
        this.placedAlgoliaEles = this.placedAlgoliaEles.filter(
          (ele) => ele !== (ev.detail as HTMLElement)
        );
        if (!this.placedAlgoliaEles.length) {
          setTimeout(() => resolve('tpl updated'), 500);
          outputTo.removeEventListener('nanoTplUpdated', childrenResolver);
        }
      };
      outputTo.addEventListener('nanoTplUpdated', childrenResolver);
    });
  }

  // Stencil hooks

  componentWillLoad() {
    readTask((_) => {
      this.tplSlot = getDirectChildren(this.host, '[slot="template"]')[0];
      this.outputSlot = getDirectChildren(this.host, '[slot="output"]')[0];
      this.slottedInput = getDirectChildren(
        this.host,
        '[slot="search-input"]'
      )[0];
      this.hasLoadSlot = !!getDirectChildren(this.host, '[slot="loader"]')[0];
      if (!!this.tplSlot) this.tpl = this.tplSlot.innerHTML;

      // get input field to listen to
      if (
        this.slottedInput &&
        (this.slottedInput.tagName.toLocaleLowerCase() === 'nano-input' ||
          this.slottedInput instanceof HTMLInputElement)
      ) {
        this.inputField = this.slottedInput as HTMLNanoInputElement;
      } else if (this.slottedInput) {
        this.inputField = this.slottedInput.querySelector(
          'nano-input, input:not([type="radio"]):not([type="checkbox"])'
        );
      }

      writeTask((_) => {
        if (this.showResults && this.tpl) this.searchChange();
      });
    });

    this.searchIndexNameChange();
    this.initAlgoliaClient();
    this.initAlgoliaIndex();

    // Create wormwhole shared 'universe' (https://github.com/mihar-22/stencil-wormhole)
    Universe.create(this, this.wormholeState);

    if (this.storeId) {
      ComponentStore.init(
        this,
        ['query', 'replicaIndex'],
        this.storeMethod,
        this.storeId
      );
      ComponentStore.init(
        this,
        ['apiKey', 'appId', 'searchIndex'],
        'session',
        this.storeId + '_api'
      );
    }
    this.internalIndexSwitch();
  }

  connectedCallback() {
    this.searchChange = debounce(this.searchChange, 300);
    this.browseIndexChange();

    if (!Build.isBrowser) return;
    this.host.dispatchEvent(
      new CustomEvent('nanoDidLoad', {
        detail: this.host,
      })
    );
  }

  render() {
    const wormholeState = {
      indexResults: this.indexResults,
      results: this.currentHits,
      filterChanged: this.filterChanged,
      tplRenderFn: this.tplRenderFn,
      resultsPage: this.resultsPage,
      changeEvent: this.changeEvent,
      facets: this.facets,
      algoliaIndex: this.algoliaIndex,
      searchIndex: this.searchIndex,
      globalStoreMethod: this.storeMethod,
      isLoading: this.isLoading,
    };
    return (
      <Host
        class={{
          'show-results': this.showResults,
          'is-loading': this.isLoading,
        }}
        aria-busy={this.isLoading}
      >
        {this.hasLoadSlot && (
          <div hidden={!this.isLoading}>
            <slot name="loader" />
          </div>
        )}
        {!this.hasLoadSlot && (
          <div
            class={{
              loader: true,
              loading: this.isLoading,
            }}
          >
            <nano-spinner overlay={true} type="circle"></nano-spinner>
          </div>
        )}

        <Universe.Provider state={wormholeState}>
          <slot name="search-input"></slot>
          <div class="results-container" aria-live="polite">
            <div ref={(div) => (this.resultsDiv = div)} class="results">
              <slot name="output" />
            </div>
          </div>
        </Universe.Provider>

        <div
          ref={(div) => (this.defaultDiv = div)}
          class="default"
          tabindex="-1"
        >
          <slot />
        </div>
      </Host>
    );
  }
}
